import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useTenantStore } from "../../stores/tenant";
import { Badge } from "../../components/ui/badge";
import type { Booking } from "@shared/types";

export function BookingsList() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  const [bookings, setBookings] = useState<Booking[]>([]);

  useEffect(() => {
    if (!tenantId) return;
    api<Booking[]>(`/api/tenants/${tenantId}/bookings`)
      .then(setBookings)
      .catch(() => setBookings([]));
  }, [tenantId]);

  return (
    <div>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="page-header__title">Réservations</h1>
          <p className="page-header__subtitle">Les réservations prises par les agents vocaux.</p>
        </div>
      </header>
      <table className="table">
        <thead>
          <tr>
            <th>Date prévue</th>
            <th>Client</th>
            <th>Téléphone</th>
            <th>Type</th>
            <th>Détails</th>
            <th>Statut</th>
          </tr>
        </thead>
        <tbody>
          {bookings.length === 0 ? (
            <tr>
              <td colSpan={6} className="table__empty">
                Aucune réservation
              </td>
            </tr>
          ) : (
            bookings.map((b) => (
              <tr key={b.id}>
                <td>{formatScheduledAt(b.scheduledAt)}</td>
                <td>{b.customerName}</td>
                <td style={{ fontFamily: "var(--mono)" }}>{b.customerPhone}</td>
                <td>{b.kind}</td>
                <td>{b.partySize ? `${b.partySize} pers.` : "—"} {b.notes ? `(${b.notes})` : ""}</td>
                <td>
                  <BookingStatusBadge status={b.status} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function formatScheduledAt(value: Booking["scheduledAt"]): string {
  if (value == null) return "—";
  const d =
    typeof value === "number"
      ? new Date(value < 1e12 ? value * 1000 : value)
      : new Date(value);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("fr-CA");
}

function BookingStatusBadge({ status }: { status: Booking["status"] }) {
  const map: Record<Booking["status"], { v: Parameters<typeof Badge>[0]["variant"]; l: string }> = {
    pending: { v: "warning", l: "En attente" },
    confirmed: { v: "success", l: "Confirmée" },
    cancelled: { v: "neutral", l: "Annulée" },
    no_show: { v: "danger", l: "No-show" },
  };
  return <Badge variant={map[status].v}>{map[status].l}</Badge>;
}
